import type { KeyboardEvent, ReactElement, ReactNode } from 'react';
import { useEffect, useId, useRef } from 'react';
import { twMerge } from 'cn';
import { Sel, STagNew } from '#~/stag/index.js';

export type SetupNoticeKind = 'permission' | 'link_failed' | 'add_unavailable';

export interface SetupNoticeModalProps {
  readonly kind: SetupNoticeKind;
  readonly studioName: string;
  readonly computerName: string;
  readonly roleLabel?: string | undefined;
  /** Committed Desktop Bridges already on the Studio ('add_unavailable'). */
  readonly machineNames?: readonly string[] | undefined;
  readonly busy?: boolean;
  readonly onSecondary: () => void;
  readonly onPrimary: () => void;
}

const LockIcon = (): ReactElement => (
  <svg aria-hidden='true' className='size-[16px] fill-muted' viewBox='0 0 10.5 11.5'>
    <path d='M 5.25,0 C 4.3883388,0 3.5604622,0.3418815 2.9511719,0.95117188 2.3418815,1.5604622 2,2.3883388 2,3.25 V 4.5 H 1.75 C 0.79238405,4.5 0,5.2923841 0,6.25 v 3.5 c 0,0.957621 0.79240215,1.75 1.75,1.75 h 7 c 0.9576031,0 1.75,-0.792397 1.75,-1.75 V 6.25 C 10.5,5.2924022 9.7076212,4.5 8.75,4.5 H 8.5 V 3.25 C 8.5,2.3883388 8.1581185,1.5604622 7.5488281,0.95117188 6.9395378,0.3418815 6.1116612,0 5.25,0 Z m 0,1.5 C 5.714416,1.5 6.1598936,1.683331 6.4882812,2.0117188 6.816669,2.3401064 7,2.785584 7,3.25 V 4.5 H 3.5 V 3.25 C 3.5,2.785584 3.6833311,2.3401064 4.0117188,2.0117188 4.3401065,1.683331 4.785584,1.5 5.25,1.5 Z M 1.75,6 h 1 5 1 C 8.8969766,6 9,6.10304 9,6.25 v 3.5 C 9,9.8969947 8.8969947,10 8.75,10 h -7 C 1.60304,10 1.5,9.8969766 1.5,9.75 V 6.25 C 1.5,6.1030581 1.6030581,6 1.75,6 Z' />
  </svg>
);

const ErrorIcon = (): ReactElement => (
  <svg aria-hidden='true' className='size-20 fill-error' viewBox='0 0 24 24'>
    <path d='M12 1.2C6.1 1.2 1.2 6.1 1.2 12S6.1 22.8 12 22.8 22.8 17.9 22.8 12 17.9 1.2 12 1.2m0 20c-5.1 0-9.2-4.1-9.2-9.2S6.9 2.8 12 2.8s9.2 4.1 9.2 9.2-4.1 9.2-9.2 9.2m0-8.2c.6 0 1-.4 1-1V8c0-.6-.4-1-1-1s-1 .4-1 1v4c0 .6.4 1 1 1m0 2c-.6 0-1 .4-1 1s.5 1 1 1 1-.4 1-1-.4-1-1-1' />
  </svg>
);

const InfoIcon = (): ReactElement => (
  <svg aria-hidden='true' className='size-[18px] fill-muted' viewBox='0 0 24 24'>
    <path d='M12 1.2C6.1 1.2 1.2 6.1 1.2 12S6.1 22.8 12 22.8 22.8 17.9 22.8 12 17.9 1.2 12 1.2m0 20c-5.1 0-9.2-4.1-9.2-9.2S6.9 2.8 12 2.8s9.2 4.1 9.2 9.2-4.1 9.2-9.2 9.2m0-10.2c-.6 0-1 .4-1 1v4c0 .6.4 1 1 1s1-.4 1-1v-4c0-.6-.4-1-1-1m0-4c-.6 0-1 .4-1 1s.4 1 1 1 1-.4 1-1-.4-1-1-1' />
  </svg>
);

// Named Bridges read as a sentence: "Office Mac", "Office Mac and Studio PC",
// "Office Mac, Studio PC and Laptop". Never a bare count.
const listNames = (names: readonly string[]): string => {
  if (names.length === 0) return 'another computer';
  if (names.length === 1) return names[0]!;
  return `${names.slice(0, -1).join(', ')} and ${names.at(-1)!}`;
};

const secondaryButtonClass =
  'h-[32px] shrink-0 whitespace-nowrap rounded-6 border border-white/10 bg-base-200 px-[14px] text-[13px] font-medium text-light shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06)] hover:bg-base-50 focus-visible:outline-none focus-visible:ring focus-visible:ring-focus/50 disabled:opacity-50';

const primaryButtonClass =
  'h-[32px] shrink-0 whitespace-nowrap rounded-6 bg-primary px-[14px] text-[13px] font-demibold text-white hover:bg-primary-highlight focus-visible:outline-none focus-visible:ring focus-visible:ring-focus/50 disabled:opacity-50';

/**
 * Signed-off Bridge Setup overlays — presentation only. 'permission' and
 * 'link_failed' are the approved states; 'add_unavailable' (ENG-4483 W3) is
 * the gated-addition explanation that replaced the removed replacement modal
 * and is subject to the W1 Design addendum before release.
 */
export const SetupNoticeModal = ({
  kind,
  studioName,
  computerName,
  roleLabel,
  machineNames = [],
  busy = false,
  onSecondary,
  onPrimary
}: SetupNoticeModalProps): ReactElement => {
  const modalRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const idSeed = useId().replaceAll(':', '');
  const titleId = `setup-notice-title-${idSeed}`;
  const descriptionId = `setup-notice-description-${idSeed}`;
  const isPermission = kind === 'permission';
  const isAddUnavailable = kind === 'add_unavailable';
  const modalStag = isPermission
    ? 'permission_modal'
    : isAddUnavailable
      ? 'add_unavailable_modal'
      : 'link_failed_modal';
  const secondaryStag = isPermission
    ? 'permission_back'
    : isAddUnavailable
      ? 'add_unavailable_back'
      : 'link_failed_cancel';
  const primaryStag = isPermission
    ? 'permission_create'
    : isAddUnavailable
      ? 'add_unavailable_create'
      : 'link_failed_retry';

  useEffect(() => {
    returnFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    modalRef.current?.focus();
    return () => returnFocusRef.current?.focus();
  }, []);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      if (!busy) onSecondary();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = [
      ...(modalRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled)') ?? [])
    ];
    if (focusable.length === 0) return;
    const first = focusable[0]!;
    const last = focusable.at(-1)!;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  let body: ReactNode;
  if (isAddUnavailable) {
    // Truthful and non-destructive: name the existing Bridge(s), say nothing
    // changed, and point at the one action setup does offer.
    body = (
      <>
        <p className='m-0 text-[13px] leading-[1.55] text-muted'>
          {studioName} is already linked to {listNames(machineNames)}. Adding {computerName}{' '}
          alongside {machineNames.length > 1 ? 'them' : 'it'} isn&apos;t available yet.
        </p>
        <p className='mb-20 mt-6 text-[13px] leading-[1.55] text-secondary'>
          Nothing was changed. You can set this computer up with its own studio.
        </p>
      </>
    );
  } else if (isPermission) {
    const ask = `Ask an owner or administrator to link ${computerName}, or set this computer up with its own studio.`;
    body =
      roleLabel === undefined ? (
        ask
      ) : (
        <>
          Your role in {studioName} is {roleLabel}. {ask}
        </>
      );
  } else {
    body = (
      <>
        <p className='m-0 text-[13px] leading-[1.55] text-muted'>
          {computerName} wasn&apos;t linked to {studioName}.
        </p>
        <p className='mb-20 mt-6 text-[13px] leading-[1.55] text-secondary'>
          The studio wasn&apos;t changed.
        </p>
      </>
    );
  }

  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black/[0.55] p-24'
      {...STagNew(Sel.StudioPicker, 'notice_backdrop')}
      onClick={(event): void => {
        if (event.target === event.currentTarget && !busy) onSecondary();
      }}
      onKeyDown={handleKeyDown}
    >
      <div
        ref={modalRef}
        role='dialog'
        aria-modal='true'
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        aria-busy={busy}
        tabIndex={-1}
        // See knowledge: design-system-tailwind-color-tokens (studio.client) — named colors emit no CSS.
        className='w-full max-w-[400px] rounded-12 bg-base-100 p-24 text-center text-light shadow-[0_16px_48px_rgba(0,0,0,0.6),0_2px_8px_rgba(0,0,0,0.5),inset_0_0_0_1px_rgba(255,255,255,0.06)] focus:outline-none'
        {...STagNew(Sel.StudioPicker, modalStag)}
      >
        <span
          className={twMerge(
            'mx-auto mb-[14px] flex size-40 items-center justify-center rounded-full',
            isPermission || isAddUnavailable ? 'bg-secondary/20' : 'bg-error/15'
          )}
        >
          {isPermission ? <LockIcon /> : isAddUnavailable ? <InfoIcon /> : <ErrorIcon />}
        </span>
        <h2
          id={titleId}
          className='mb-10 text-[20px] font-demibold leading-[1.2] tracking-[-0.01em]'
        >
          {isPermission
            ? 'Only an owner or admin can link a bridge'
            : isAddUnavailable
              ? "Adding another computer isn't available yet"
              : "Couldn't link this computer"}
        </h2>
        <div
          id={descriptionId}
          className={isPermission ? 'mb-20 text-[13px] leading-[1.55] text-muted' : ''}
        >
          {body}
        </div>
        <div className='flex justify-center gap-10'>
          <button
            type='button'
            disabled={busy}
            className={secondaryButtonClass}
            {...STagNew(Sel.StudioPicker, secondaryStag)}
            onClick={onSecondary}
          >
            {isPermission || isAddUnavailable ? 'Back' : 'Cancel'}
          </button>
          <button
            type='button'
            disabled={busy}
            className={primaryButtonClass}
            {...STagNew(Sel.StudioPicker, primaryStag)}
            onClick={onPrimary}
          >
            {isPermission || isAddUnavailable ? 'Create a new studio' : 'Try again'}
          </button>
        </div>
      </div>
    </div>
  );
};
